<template>
  <div class="cc-user-panel" :class="{ compact: compact }">
    <div class="cc-panel-toolbar">
      <el-input
        v-model="keyword"
        clearable
        prefix-icon="el-icon-search"
        placeholder="搜索姓名 / 工号 / 部门"
        size="small"
        class="cc-search"
      />
      <span class="cc-selected-count">已选 {{ selectedIds.length }} 人</span>
    </div>

    <div v-if="selectedUsers.length" class="cc-selected-chips">
      <div v-for="u in selectedUsers" :key="'sel-' + u.id" class="cc-chip" @click="toggle(u.id)">
        <user-avatar
          :name="u.name"
          :username="u.username"
          :avatar="u.avatar"
          :user-id="u.id"
          size="sm"
        />
        <span class="cc-chip-name">{{ u.name || ('用户' + u.id) }}</span>
        <span
          v-if="u.departmentName"
          class="cc-dept-tag"
          :style="deptStyle(u.departmentName)"
        >{{ u.departmentName }}</span>
        <i class="el-icon-close cc-chip-remove"></i>
      </div>
    </div>

    <div class="cc-list" v-loading="loading">
      <div
        v-for="u in filteredOptions"
        :key="u.id"
        class="cc-row"
        :class="{ active: isSelected(u.id) }"
        @click="toggle(u.id)"
      >
        <el-checkbox :value="isSelected(u.id)" @click.native.prevent />
        <user-avatar
          :name="u.name"
          :username="u.username"
          :avatar="u.avatar"
          :user-id="u.id"
          size="md"
        />
        <div class="cc-row-main">
          <span class="cc-name">{{ u.name }}</span>
          <span
            v-if="u.departmentName"
            class="cc-dept-tag"
            :style="deptStyle(u.departmentName)"
          >{{ u.departmentName }}</span>
        </div>
        <span class="cc-username">{{ u.username }}</span>
      </div>
      <div v-if="!loading && !filteredOptions.length" class="cc-empty">
        没有匹配的人员
      </div>
    </div>
  </div>
</template>

<script>
import UserAvatar from "./UserAvatar.vue";

const DEPT_PALETTE = [
  { color: "#1d6fd8", bg: "#e8f1fc" },
  { color: "#0d9488", bg: "#e6f7f5" },
  { color: "#c2410c", bg: "#fff1e8" },
  { color: "#7c3aed", bg: "#f3edff" },
  { color: "#b45309", bg: "#fff7e6" },
  { color: "#be123c", bg: "#ffe8ee" },
  { color: "#0369a1", bg: "#e6f4fb" },
  { color: "#15803d", bg: "#e8f8ee" },
  { color: "#a16207", bg: "#faf3e0" },
  { color: "#475569", bg: "#eef1f5" },
];

function hashStr(s) {
  let h = 0;
  const str = String(s || "");
  for (let i = 0; i < str.length; i++) {
    h = (h * 31 + str.charCodeAt(i)) >>> 0;
  }
  return h;
}

function toId(v) {
  const n = Number(v);
  return isNaN(n) ? null : n;
}

export default {
  name: "CcUserPanel",
  components: { UserAvatar },
  props: {
    value: { type: Array, default: () => [] },
    options: { type: Array, default: () => [] },
    /** 已选用户详情（含 name），用于 options 里暂时没有的人 */
    selectedDetails: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    compact: { type: Boolean, default: false },
  },
  data() {
    return { keyword: "" };
  },
  computed: {
    selectedIds() {
      return (this.value || []).map(toId).filter((id) => id != null);
    },
    userMap() {
      const map = {};
      (this.options || []).forEach((u) => {
        const id = toId(u.id);
        if (id != null) map[id] = { ...u, id };
      });
      (this.selectedDetails || []).forEach((u) => {
        const id = toId(u.id);
        if (id != null) {
          map[id] = { ...(map[id] || {}), ...u, id };
        }
      });
      return map;
    },
    selectedUsers() {
      return this.selectedIds.map((id) => this.userMap[id] || { id, name: "" });
    },
    filteredOptions() {
      const kw = (this.keyword || "").trim().toLowerCase();
      const list = (this.options || []).map((u) => ({ ...u, id: toId(u.id) })).filter((u) => u.id != null);
      if (!kw) return list;
      return list.filter((u) => {
        const hay = [u.name, u.username, u.departmentName]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.indexOf(kw) >= 0;
      });
    },
  },
  methods: {
    isSelected(id) {
      return this.selectedIds.indexOf(toId(id)) >= 0;
    },
    toggle(id) {
      const nid = toId(id);
      if (nid == null) return;
      const next = this.selectedIds.slice();
      const idx = next.indexOf(nid);
      if (idx >= 0) next.splice(idx, 1);
      else next.push(nid);
      this.$emit("input", next);
    },
    deptStyle(deptName) {
      const p = DEPT_PALETTE[hashStr(deptName) % DEPT_PALETTE.length];
      return {
        color: p.color,
        backgroundColor: p.bg,
        borderColor: p.color,
      };
    },
  },
};
</script>

<style scoped>
.cc-user-panel {
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}
.cc-panel-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid #ebeef5;
  background: #fafbfc;
}
.cc-search {
  flex: 1;
}
.cc-selected-count {
  flex-shrink: 0;
  font-size: 13px;
  color: #606266;
}
.cc-selected-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 14px;
  border-bottom: 1px solid #ebeef5;
  max-height: 110px;
  overflow-y: auto;
}
.cc-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px 4px 4px;
  background: #f5f7fa;
  border: 1px solid #e4e7ed;
  border-radius: 16px;
  cursor: pointer;
  font-size: 13px;
}
.cc-chip:hover {
  border-color: #f56c6c;
  background: #fef0f0;
}
.cc-chip-name {
  color: #303133;
}
.cc-chip-remove {
  color: #909399;
  font-size: 12px;
}
.cc-list {
  height: 420px;
  overflow-y: auto;
}
.cc-user-panel.compact .cc-list {
  height: 280px;
}
.cc-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-bottom: 1px solid #f2f3f5;
  cursor: pointer;
  transition: background 0.15s;
}
.cc-row:hover {
  background: #f5f7fa;
}
.cc-row.active {
  background: #ecf5ff;
}
.cc-row-main {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
}
.cc-name {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
  white-space: nowrap;
}
.cc-dept-tag {
  font-size: 12px;
  border-radius: 3px;
  padding: 0 6px;
  line-height: 18px;
  border: 1px solid transparent;
  white-space: nowrap;
  flex-shrink: 0;
}
.cc-username {
  font-size: 12px;
  color: #909399;
  flex-shrink: 0;
}
.cc-empty {
  padding: 48px 16px;
  text-align: center;
  color: #909399;
  font-size: 14px;
}
</style>
